import clsx from 'clsx';
import { m, LazyMotion, domAnimation } from 'framer-motion';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import Link from 'components/shared/link';
import DEFAULT_EASE from 'constants/default-ease';

import DataIcon from './images/data.inline.svg';
import EngineeringIcon from './images/engineering.inline.svg';
import MarketingIcon from './images/marketing.inline.svg';
import ProductIcon from './images/product.inline.svg';
import SupportIcon from './images/support.inline.svg';

const icons = {
  'marketing-icon': MarketingIcon,
  'product-icon': ProductIcon,
  'engineering-icon': EngineeringIcon,
  'support-icon': SupportIcon,
  'data-icon': DataIcon,
};

const cardSize = {
  ul: {
    xl: 'grid-cols-12',
    lg: 'grid-cols-12',
    md: 'grid-cols-12',
    sm: 'grid-cols-10',
  },
  li: {
    xl: 'col-span-6',
    lg: 'col-span-4',
    md: 'col-span-3',
    sm: 'col-span-2',
  },
};

const SectionWithCardsAndIcons = ({
  title,
  theme,
  features,
  withBackground,
  withBackgroundIllustration,
}) => {
  const [itemsWrapperRef, isItemsWrapperInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const size = useMemo(() => {
    let result = '';

    if (features.length === 5) {
      result = 'sm';
    }
    if (features.length === 4) {
      result = 'md';
    }
    if (features.length === 3) {
      result = 'lg';
    }
    if (features.length === 2) {
      result = 'xl';
    }

    return result;
  }, [features.length]);

  return (
    <section
      className={clsx('safe-paddings relative py-40 xl:py-30 lg:py-20 md:py-14', {
        'bg-gray-1': withBackground === true,
      })}
      ref={itemsWrapperRef}
    >
      <div
        className={clsx(
          'absolute top-0 left-0 flex h-full w-full flex-col justify-center lg:hidden',
          {
            hidden: withBackgroundIllustration === false,
          }
        )}
      >
        <LazyMotion features={domAnimation}>
          <m.div
            className="absolute right-[12%] bottom-[10%] h-60 w-60 rounded-full bg-primary-1"
            initial={{ scale: 0 }}
            animate={isItemsWrapperInView && { scale: 1 }}
            transition={{ duration: 1, ease: DEFAULT_EASE }}
            aria-hidden
          />
          <svg
            className="h-auto w-full"
            viewBox="0 0 1920 592"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <m.path
              d="M-171 37.9706C254.048 -65.4617 749.077 51.7338 275.38 344.436C149.741 422.07 134.678 551.366 564.925 582.84C1102.73 622.183 1614.33 517.821 1927 300.814"
              stroke="#f25a5a"
              strokeWidth="2"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={isItemsWrapperInView && { pathLength: 1 }}
              transition={{ duration: 2, ease: DEFAULT_EASE }}
            />
          </svg>
        </LazyMotion>
      </div>

      <div
        className={clsx(
          'container relative flex flex-col items-center lg:max-w-[584px] md:w-full md:max-w-none',
          theme === 'red' ? 'theme-red' : 'theme-blue'
        )}
      >
        <h2
          className="text-center text-5xl font-bold leading-denser"
          dangerouslySetInnerHTML={{ __html: title }}
        />

        <ul className={clsx('grid-gap grid w-full pt-16 lg:col-span-full', cardSize.ul[size])}>
          {features.map(({ title, description, iconName, linkText, linkUrl }, index) => {
            const Icon = icons[iconName];

            return (
              <li
                className={clsx(
                  'card-shadow flex flex-col justify-between rounded-xl border border-gray-4 bg-white p-5 lg:col-span-full',
                  cardSize.li[size]
                )}
                key={index}
              >
                <div className="flex flex-col">
                  <div className="flex flex-col items-start gap-y-4 lg:flex-row lg:items-center lg:gap-x-4">
                    <Icon className="h-[45px]" aria-hidden />
                    <h3 className="text-xl font-bold">{title}</h3>
                  </div>
                  <p className="mt-4 text-base leading-relaxed">{description}</p>
                </div>
                {linkUrl && linkText && (
                  <Link className="mt-7 w-fit" to={linkUrl} size="sm" theme="black-underline">
                    {linkText}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

SectionWithCardsAndIcons.propTypes = {
  title: PropTypes.string.isRequired,
  theme: PropTypes.string.isRequired,
  features: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      iconName: PropTypes.string.isRequired,
      linkText: PropTypes.string,
      linkUrl: PropTypes.string,
    })
  ).isRequired,
  withBackground: PropTypes.bool.isRequired,
  withBackgroundIllustration: PropTypes.bool.isRequired,
};

export default SectionWithCardsAndIcons;
